import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { FileText, Calendar, User } from 'lucide-react';
import DentalChart from '../../components/DentalChart';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TreatmentHistory = () => {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchTreatments(); }, []);
  const fetchTreatments = async () => {
    try { const res = await axios.get(`${API}/patients/treatments`, { headers: { Authorization: `Bearer ${token}` } }); setTreatments(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const treatedTeethMap = {};
  const toothCounts = {};
  treatments.forEach(tr => {
    (tr.teeth || []).forEach(toothNum => {
      const info = `${tr.treatment_type} (${new Date(tr.date).toLocaleDateString()})`;
      treatedTeethMap[toothNum] = treatedTeethMap[toothNum] ? treatedTeethMap[toothNum] + '\n' + info : info;
      toothCounts[toothNum] = (toothCounts[toothNum] || 0) + 1;
    });
  });
  const multiTreatTeeth = Object.entries(toothCounts).filter(([, c]) => c > 1).map(([t]) => parseInt(t));

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F4C5C]"></div></div>;

  return (
    <div className="p-6 lg:p-8" data-testid="treatment-history">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#1F2937] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{t.treatmentHistory}</h1>
        <p className="text-[#6B7280]">{t.treatmentHistorySubtitle}</p>
      </div>

      {Object.keys(treatedTeethMap).length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-8" data-testid="dental-chart-section">
          <h2 className="text-lg font-bold text-[#1F2937] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>{t.yourDentalChart}</h2>
          <p className="text-sm text-[#6B7280] mb-2">{t.teethHighlighted}</p>
          {multiTreatTeeth.length > 0 && (
            <div className="flex flex-wrap gap-3 mb-4" data-testid="patient-chart-legend">
              <div className="flex items-center gap-1.5 text-xs">
                <span className="w-3 h-3 rounded-sm" style={{ background: '#E07A5F' }}></span>
                <span className="text-[#6B7280]">Treated</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs">
                <span className="w-3 h-3 rounded-sm" style={{ background: '#22C55E' }}></span>
                <span className="text-[#6B7280]">Multiple Treatments</span>
              </div>
            </div>
          )}
          <DentalChart treatedTeethMap={treatedTeethMap} multiTreatTeeth={multiTreatTeeth} />
        </div>
      )}

      {treatments.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center" data-testid="no-treatments">
          <FileText className="w-16 h-16 text-[#6B7280] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-[#1F2937] mb-2">{t.noTreatmentHistory}</h3>
          <p className="text-[#6B7280]">{t.noTreatmentHistoryDesc}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="treatments-table">
              <thead className="bg-[#F9FAFB] border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">{t.date}</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">{t.treatmentType}</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">{t.doctor}</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">{t.teeth}</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">{t.notes}</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-[#1F2937]">{t.cost}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {treatments.map((treatment, index) => (
                  <tr key={treatment.id} className="hover:bg-[#F9FAFB] transition-colors" data-testid={`treatment-row-${index}`}>
                    <td className="px-6 py-4"><div className="flex items-center text-sm"><Calendar className="w-4 h-4 mr-2 text-[#6B7280]" /><span className="text-[#1F2937]">{new Date(treatment.date).toLocaleDateString()}</span></div></td>
                    <td className="px-6 py-4"><span className="text-sm font-medium text-[#1F2937]">{treatment.treatment_type}</span></td>
                    <td className="px-6 py-4"><div className="flex items-center text-sm"><User className="w-4 h-4 mr-2 text-[#6B7280]" /><span className="text-[#1F2937]">{treatment.doctor}</span></div></td>
                    <td className="px-6 py-4">
                      {treatment.teeth && treatment.teeth.length > 0 ? (
                        <div className="flex flex-wrap gap-1">{treatment.teeth.sort((a,b) => a-b).map(t => (<span key={t} className="inline-block bg-[#E07A5F]/15 text-[#E07A5F] text-xs font-semibold px-2 py-0.5 rounded">#{t}</span>))}</div>
                      ) : <span className="text-sm text-[#6B7280]">-</span>}
                    </td>
                    <td className="px-6 py-4"><span className="text-sm text-[#6B7280]">{treatment.notes}</span></td>
                    <td className="px-6 py-4 text-right"><span className="text-sm font-semibold text-[#1F2937]">${treatment.cost.toFixed(2)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TreatmentHistory;
